import { resolveChoiceCardAction } from "./truth-or-dare-choice-interaction";
import type {
  HostedGameSnapshot,
  HostedPartySnapshot,
} from "@cue-the-night/transport";

import {
  HostedPartyClient,
  HostedPartyClientError,
} from "./hosted-party-client";

import {
  gameplayHostAsset,
  gameplayHostName,
  playerAvatarAsset,
  playerRingAsset,
} from "./premium-gameplay-assets";

import {
  activateGameVisual,
} from "./visual-foundation";

type TruthOrDareChoice =
  | "truth"
  | "dare";

interface TruthOrDarePromptView {
  readonly id: string;
  readonly type:
    TruthOrDareChoice;
  readonly text: string;
}

interface TruthOrDarePublicState {
  readonly phase:
    | "SETUP"
    | "CHOICE"
    | "OUTCOME";

  readonly activePlayerId:
    string;

  readonly turnNumber:
    number;

  readonly completedTurns:
    number;

  readonly currentChoice:
    TruthOrDareChoice | null;

  readonly currentPrompt:
    TruthOrDarePromptView | null;
}

export interface PlayerGameRenderOptions {
  readonly panel:
    HTMLElement;

  readonly party:
    HostedPartySnapshot;

  readonly game:
    HostedGameSnapshot;

  readonly participantId:
    string;

  readonly client:
    HostedPartyClient;

  readonly onGameSnapshot:
    (
      snapshot:
        HostedGameSnapshot,
    ) => void;
}

export interface OwnerGameRenderOptions {
  readonly panel:
    HTMLElement;

  readonly party:
    HostedPartySnapshot;

  readonly game:
    HostedGameSnapshot;
}

function isRecord(
  value: unknown,
): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value)
  );
}

function escapeHtml(
  value: string,
): string {
  const element =
    document.createElement(
      "div",
    );

  element.textContent =
    value;

  return element.innerHTML;
}

function readChoice(
  value:
    unknown,
): TruthOrDareChoice | null {
  if (
    value === "truth" ||
    value === "dare"
  ) {
    return value;
  }

  return null;
}

function readPrompt(
  value:
    unknown,
): TruthOrDarePromptView | null {
  if (
    value === null ||
    value === undefined
  ) {
    return null;
  }

  if (
    !isRecord(value) ||
    typeof value.id !==
      "string" ||
    (
      value.type !==
        "truth" &&
      value.type !==
        "dare"
    ) ||
    typeof value.text !==
      "string"
  ) {
    throw new Error(
      "Truth or Dare prompt state is invalid.",
    );
  }

  return {
    id:
      value.id,
    type:
      value.type,
    text:
      value.text,
  };
}

export function readTruthOrDareState(
  snapshot:
    HostedGameSnapshot,
): TruthOrDarePublicState {
  if (
    snapshot.gameId !==
    "truth-or-dare"
  ) {
    throw new Error(
      `Unsupported game: ${snapshot.gameId}`,
    );
  }

  const value =
    snapshot.publicState;

  if (
    !isRecord(value) ||
    (
      value.phase !==
        "SETUP" &&
      value.phase !==
        "CHOICE" &&
      value.phase !==
        "OUTCOME"
    ) ||
    typeof value.activePlayerId !==
      "string" ||
    !Number.isInteger(
      value.turnNumber,
    ) ||
    !Number.isInteger(
      value.completedTurns,
    )
  ) {
    throw new Error(
      "Truth or Dare public state is invalid.",
    );
  }

  return {
    phase:
      value.phase,

    activePlayerId:
      value.activePlayerId,

    turnNumber:
      value.turnNumber as
        number,

    completedTurns:
      value.completedTurns as
        number,

    currentChoice:
      readChoice(
        value.currentChoice,
      ),

    currentPrompt:
      readPrompt(
        value.currentPrompt,
      ),
  };
}

function playerName(
  party:
    HostedPartySnapshot,
  participantId:
    string,
): string {
  return (
    party.participants.find(
      (participant) =>
        participant.participantId ===
        participantId,
    )?.displayName ??
    "Player"
  );
}

function playerAvatarId(
  party:
    HostedPartySnapshot,
  participantId:
    string,
): string {
  return (
    party.participants.find(
      (participant) =>
        participant.participantId ===
        participantId,
    )?.avatarId ??
    "avatar-001"
  );
}

function choiceLabel(
  choice:
    TruthOrDareChoice,
): string {
  return choice ===
    "truth"
    ? "TRUTH"
    : "DARE";
}

export function renderPlayerTruthOrDare(
  options:
    PlayerGameRenderOptions,
): void {
  const state =
    readTruthOrDareState(
      options.game,
    );

  activateGameVisual(
    "truth-or-dare",
    state.currentPrompt?.type ??
      state.currentChoice ??
      "choice",
  );

  const activeName =
    playerName(
      options.party,
      state.activePlayerId,
    );

  const isActivePlayer =
    state.activePlayerId ===
    options.participantId;

  const activeAvatar =
    playerAvatarAsset(
      playerAvatarId(
        options.party,
        state.activePlayerId,
      ),
    );

  const prompt =
    state.currentPrompt;

  const hostMoment =
    prompt
      ? "reveal"
      : state.currentChoice
        ? "suspense"
        : "presenting";

  const hostName =
    gameplayHostName(
      options.party
        .activeVirtualHostId,
    );

  const hostAsset =
    gameplayHostAsset(
      options.party
        .activeVirtualHostId,
      hostMoment,
    );

  let actions = "";

  if (
    state.phase ===
      "CHOICE" &&
    isActivePlayer
  ) {
    actions = `
      <div class="game-actions game-choice-grid">
        <button
          class="game-action truth-action ${
            state.currentChoice === "truth"
              ? "selected-choice"
              : state.currentChoice
                ? "choice-dimmed"
                : ""
          }"
          data-command="truth"
          type="button"
          aria-pressed="${
            state.currentChoice === "truth"
              ? "true"
              : "false"
          }"
        >
          <span
            class="choice-card-symbol"
            aria-hidden="true"
          >
            ?
          </span>

          <span class="choice-card-copy">
            <strong>
              Truth
            </strong>

            <small>
              ${
                state.currentChoice === "truth"
                  ? "Tap again to reveal"
                  : state.currentChoice
                    ? "Switch to Truth"
                    : "Answer honestly."
              }
            </small>
          </span>
        </button>

        <button
          class="game-action dare-action ${
            state.currentChoice === "dare"
              ? "selected-choice"
              : state.currentChoice
                ? "choice-dimmed"
                : ""
          }"
          data-command="dare"
          type="button"
          aria-pressed="${
            state.currentChoice === "dare"
              ? "true"
              : "false"
          }"
        >
          <span
            class="choice-card-symbol"
            aria-hidden="true"
          >
            !
          </span>

          <span class="choice-card-copy">
            <strong>
              Dare
            </strong>

            <small>
              ${
                state.currentChoice === "dare"
                  ? "Tap again to reveal"
                  : state.currentChoice
                    ? "Switch to Dare"
                    : "Commit to it."
              }
            </small>
          </span>
        </button>
      </div>

      <button
        class="game-action secondary-action surprise-action"
        data-command="surprise"
        type="button"
      >
        Surprise Me
      </button>

      ${
        state.currentChoice
          ? `
            <button
              class="game-action reveal-action"
              data-command="reveal"
              type="button"
            >
              Reveal ${choiceLabel(state.currentChoice)}
            </button>
          `
          : `
            <button
              class="game-action secondary-action"
              data-command="pass"
              type="button"
            >
              Player Pass
            </button>
          `
      }
    `;
  }

  if (
    state.phase ===
      "OUTCOME" &&
    isActivePlayer &&
    prompt
  ) {
    if (
      prompt.type ===
      "truth"
    ) {
      actions = `
        <div class="game-actions">
          <button
            class="game-action"
            data-command="answered"
            type="button"
          >
            Answered
          </button>

          <button
            class="game-action secondary-action"
            data-command="not_applicable"
            type="button"
          >
            N/A
          </button>

          <button
            class="game-action secondary-action"
            data-command="skip"
            type="button"
          >
            Skip
          </button>
        </div>
      `;
    } else {
      actions = `
        <div class="game-actions">
          <button
            class="game-action"
            data-command="nailed"
            type="button"
          >
            Nailed It
          </button>

          <button
            class="game-action secondary-action"
            data-command="failed"
            type="button"
          >
            Failed It
          </button>

          <button
            class="game-action secondary-action"
            data-command="skip"
            type="button"
          >
            Skip
          </button>
        </div>
      `;
    }
  }

  options.panel.innerHTML = `
    <div class="game-screen">
      <div class="game-topline">
        <p class="eyebrow">
          TRUTH OR DARE
        </p>

        <span class="game-turn">
          TURN ${state.turnNumber}
        </span>
      </div>

      <section
        class="premium-turn-hero ${
          isActivePlayer
            ? "is-active-player"
            : "is-waiting-player"
        }"
      >
        <div class="premium-player-identity">
          <div class="premium-avatar-orbit">
            <div class="premium-avatar-shell">
              <img
                class="premium-avatar-image"
                src="${activeAvatar}"
                alt="${escapeHtml(activeName)}"
              />

              <img
                class="premium-avatar-ring-art"
                src="${playerRingAsset}"
                alt=""
                aria-hidden="true"
              />
            </div>
          </div>

          <div class="premium-player-copy">
            <span class="premium-turn-kicker">
              ${
                isActivePlayer
                  ? "YOUR TURN"
                  : "NOW PLAYING"
              }
            </span>

            <strong class="premium-player-name">
              ${escapeHtml(activeName)}
            </strong>

            <span class="premium-player-context">
              ${
                state.currentChoice
                  ? `${choiceLabel(state.currentChoice)} selected`
                  : "Choose your fate"
              }
            </span>
          </div>
        </div>

        <aside
          class="rex-gameplay-presence"
          data-host-moment="${hostMoment}"
        >
          <img
            src="${hostAsset}"
            alt="${escapeHtml(hostName)}"
          />

          <div class="rex-presence-label">
            <span>
              HOST
            </span>

            <strong>
              ${escapeHtml(hostName.toUpperCase())}
            </strong>
          </div>
        </aside>
      </section>

      ${
        state.phase ===
          "CHOICE"
          ? `
            <div class="game-question">
              ${
                isActivePlayer
                  ? state.currentChoice
                    ? `${choiceLabel(state.currentChoice)} selected. Ready to reveal?`
                    : "Truth or Dare?"
                  : state.currentChoice
                    ? `${escapeHtml(activeName)} picked ${choiceLabel(state.currentChoice)}. Waiting for reveal…`
                    : `Waiting for ${escapeHtml(activeName)} to choose…`
              }
            </div>
          `
          : ""
      }

      ${
        prompt
          ? `
            <div
              class="prompt-card ${prompt.type}"
            >
              <span class="prompt-type">
                ${choiceLabel(prompt.type)}
              </span>

              <div class="prompt-text">
                ${escapeHtml(prompt.text)}
              </div>
            </div>
          `
          : ""
      }

      ${
        state.phase ===
          "OUTCOME" &&
        !isActivePlayer
          ? `
            <div class="waiting">
              Waiting for ${escapeHtml(activeName)}…
            </div>
          `
          : ""
      }

      ${actions}

      <p
        class="status game-status"
        id="game-status"
      ></p>
    </div>
  `;

  const buttons =
    Array.from(
      options.panel
        .querySelectorAll(
          ".game-action",
        ),
    ).filter(
      (
        element,
      ): element is HTMLButtonElement =>
        element instanceof
        HTMLButtonElement,
    );

  const status =
    options.panel
      .querySelector(
        "#game-status",
      );

  async function execute(
    command:
      Readonly<
        Record<
          string,
          unknown
        >
      >,
  ): Promise<void> {
    for (
      const button of
      buttons
    ) {
      button.disabled =
        true;
    }

    if (
      status instanceof
      HTMLElement
    ) {
      status.textContent =
        "Updating the night…";
    }

    try {
      const result =
        await options.client
          .sendGameCommand(
            options.game
              .gameSessionId,
            options.game
              .stateVersion,
            command,
          );

      options.onGameSnapshot(
        result.snapshot,
      );
    } catch (error) {
      for (
        const button of
        buttons
      ) {
        button.disabled =
          false;
      }

      if (
        status instanceof
        HTMLElement
      ) {
        status.textContent =
          error instanceof
            HostedPartyClientError
            ? error.message
            : error instanceof Error
              ? error.message
              : "Unable to update the game.";
      }
    }
  }

  for (
    const button of
    buttons
  ) {
    button.addEventListener(
      "click",
      () => {
        const command =
          button.dataset
            .command;

        if (
          command ===
            "truth" ||
          command ===
            "dare"
        ) {
          const action =
            resolveChoiceCardAction(
              command,
              button.getAttribute(
                "aria-pressed",
              ) === "true",
            );

          void execute(
            action,
          );

          return;
        }

        if (
          command ===
          "surprise"
        ) {
          void execute({
            type:
              "SURPRISE_ME",
          });

          return;
        }

        if (
          command ===
          "reveal"
        ) {
          void execute({
            type:
              "REVEAL",
          });

          return;
        }

        if (
          command ===
          "pass"
        ) {
          void execute({
            type:
              "PASS_TURN",
          });

          return;
        }

        if (
          command ===
            "answered" ||
          command ===
            "not_applicable" ||
          command ===
            "nailed" ||
          command ===
            "failed" ||
          command ===
            "skip"
        ) {
          void execute({
            type:
              "RESOLVE",
            outcome:
              command,
          });
        }
      },
    );
  }
}

export function renderOwnerTruthOrDare(
  options:
    OwnerGameRenderOptions,
): void {
  const state =
    readTruthOrDareState(
      options.game,
    );

  activateGameVisual(
    "truth-or-dare",
    state.currentPrompt?.type ??
      state.currentChoice ??
      "choice",
  );

  const activeName =
    playerName(
      options.party,
      state.activePlayerId,
    );

  const activeAvatar =
    playerAvatarAsset(
      playerAvatarId(
        options.party,
        state.activePlayerId,
      ),
    );

  const prompt =
    state.currentPrompt;

  options.panel.innerHTML = `
    <div class="game-screen owner-game-screen">
      <div class="game-topline">
        <p class="eyebrow">
          NIGHT ACTIVE · TRUTH OR DARE
        </p>

        <span class="game-turn">
          TURN ${state.turnNumber}
        </span>
      </div>

      <div class="owner-active-player premium-owner-player">
        <img
          class="owner-player-avatar"
          src="${activeAvatar}"
          alt="${escapeHtml(activeName)}"
        />

        <div>
          <span class="meta">
            ACTIVE PLAYER
          </span>

          <strong>
            ${escapeHtml(activeName)}
          </strong>
        </div>
      </div>

      ${
        state.phase ===
          "CHOICE"
          ? `
            <div class="game-question">
              ${
                state.currentChoice
                  ? `${escapeHtml(activeName)} picked ${choiceLabel(state.currentChoice)}. Waiting for reveal…`
                  : `Waiting for ${escapeHtml(activeName)} to choose Truth or Dare…`
              }
            </div>
          `
          : ""
      }

      ${
        prompt
          ? `
            <div
              class="prompt-card ${prompt.type}"
            >
              <span class="prompt-type">
                ${choiceLabel(prompt.type)}
              </span>

              <div class="prompt-text">
                ${escapeHtml(prompt.text)}
              </div>
            </div>
          `
          : ""
      }

      <div class="game-owner-meta">
        <span>
          State ${options.game.stateVersion}
        </span>

        <span>
          ${state.completedTurns}
          completed
        </span>
      </div>
    </div>
  `;
}
